import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useHistory } from "react-router-dom";

const GlobalAudioContext = createContext(null);

const LS_RATE = "audioMessageRate";

function readInitialRate() {
	const n = parseFloat(localStorage.getItem(LS_RATE) || "1");
	return Number.isFinite(n) && n > 0 ? n : 1;
}

function resolveDuration(audio) {
	if (!audio) return 0;
	const d = audio.duration;
	if (Number.isFinite(d) && d > 0) return d;
	try {
		if (audio.seekable && audio.seekable.length > 0) {
			const end = audio.seekable.end(audio.seekable.length - 1);
			if (Number.isFinite(end) && end > 0) return end;
		}
	} catch (_) {
		/* ignore */
	}
	return 0;
}

export function GlobalAudioProvider({ children }) {
	const audioRef = useRef(null);
	if (!audioRef.current && typeof Audio !== "undefined") {
		audioRef.current = new Audio();
		audioRef.current.preload = "metadata";
		// Precisa de CORS + Range no /media (S3) para o seek funcionar.
		audioRef.current.crossOrigin = "anonymous";
	}

	const [src, setSrc] = useState("");
	const [title, setTitle] = useState("");
	const [ticketId, setTicketId] = useState(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [playbackRate, setPlaybackRateState] = useState(readInitialRate);

	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) return undefined;

		const syncDuration = () => setDuration(resolveDuration(audio));
		const onTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
		const onPlay = () => setIsPlaying(true);
		const onPause = () => setIsPlaying(false);
		const onEnded = () => {
			setIsPlaying(false);
			setCurrentTime(0);
		};

		audio.addEventListener("loadedmetadata", syncDuration);
		audio.addEventListener("durationchange", syncDuration);
		audio.addEventListener("loadeddata", syncDuration);
		audio.addEventListener("canplay", syncDuration);
		audio.addEventListener("timeupdate", onTimeUpdate);
		audio.addEventListener("play", onPlay);
		audio.addEventListener("pause", onPause);
		audio.addEventListener("ended", onEnded);

		return () => {
			audio.removeEventListener("loadedmetadata", syncDuration);
			audio.removeEventListener("durationchange", syncDuration);
			audio.removeEventListener("loadeddata", syncDuration);
			audio.removeEventListener("canplay", syncDuration);
			audio.removeEventListener("timeupdate", onTimeUpdate);
			audio.removeEventListener("play", onPlay);
			audio.removeEventListener("pause", onPause);
			audio.removeEventListener("ended", onEnded);
		};
	}, []);

	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) return;
		audio.playbackRate = playbackRate;
		localStorage.setItem(LS_RATE, String(playbackRate));
	}, [playbackRate]);

	const play = useCallback(async (url, meta = {}) => {
		const audio = audioRef.current;
		if (!audio || !url) return;

		const absolute = (() => {
			try {
				return new URL(url, window.location.href).href;
			} catch (_) {
				return url;
			}
		})();

		if (audio.src !== absolute) {
			audio.src = url;
			audio.load();
		}
		setSrc(url);
		setTitle(meta.title || "Áudio");
		setTicketId(meta.ticketId != null && meta.ticketId !== "" ? meta.ticketId : null);
		try {
			await audio.play();
		} catch (_) {
			/* autoplay block */
		}
	}, []);

	const pause = useCallback(() => {
		audioRef.current?.pause();
	}, []);

	const toggle = useCallback(
		async (url, meta = {}) => {
			const audio = audioRef.current;
			if (!audio || !url) return;
			const absolute = new URL(url, window.location.href).href;
			const same =
				audio.src === absolute ||
				audio.src === url ||
				(src && (src === url || new URL(src, window.location.href).href === absolute));

			if (same && !audio.paused) {
				audio.pause();
				return;
			}
			await play(url, meta);
		},
		[play, src]
	);

	const seek = useCallback((seconds) => {
		const audio = audioRef.current;
		if (!audio) return;
		const dur = resolveDuration(audio);
		if (!Number.isFinite(seconds)) return;
		const next = dur > 0 ? Math.min(Math.max(0, seconds), dur) : Math.max(0, seconds);
		try {
			audio.currentTime = next;
			setCurrentTime(next);
		} catch (_) {
			/* seek not ready yet */
		}
	}, []);

	const seekRatio = useCallback(
		(ratio) => {
			const dur = duration || resolveDuration(audioRef.current);
			if (!(dur > 0)) return;
			seek((Number(ratio) / 100) * dur);
		},
		[duration, seek]
	);

	const cycleRate = useCallback(() => {
		setPlaybackRateState((prev) =>
			prev === 0.5 ? 1 : prev === 1 ? 1.5 : prev === 1.5 ? 2 : 0.5
		);
	}, []);

	const stop = useCallback(() => {
		const audio = audioRef.current;
		if (!audio) return;
		audio.pause();
		audio.removeAttribute("src");
		audio.load();
		setSrc("");
		setTitle("");
		setTicketId(null);
		setIsPlaying(false);
		setCurrentTime(0);
		setDuration(0);
	}, []);

	const value = useMemo(
		() => ({
			src,
			title,
			ticketId,
			isPlaying,
			currentTime,
			duration,
			playbackRate,
			play,
			pause,
			toggle,
			seek,
			seekRatio,
			cycleRate,
			stop,
			setPlaybackRate: setPlaybackRateState,
		}),
		[
			src,
			title,
			ticketId,
			isPlaying,
			currentTime,
			duration,
			playbackRate,
			play,
			pause,
			toggle,
			seek,
			seekRatio,
			cycleRate,
			stop,
		]
	);

	return (
		<GlobalAudioContext.Provider value={value}>
			{children}
			<GlobalAudioMiniBar />
		</GlobalAudioContext.Provider>
	);
}

export function useGlobalAudio() {
	const ctx = useContext(GlobalAudioContext);
	if (!ctx) {
		throw new Error("useGlobalAudio must be used within GlobalAudioProvider");
	}
	return ctx;
}

function formatTime(seconds) {
	if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
	const mins = Math.floor(seconds / 60);
	const secs = Math.floor(seconds % 60);
	return `${mins}:${String(secs).padStart(2, "0")}`;
}

function GlobalAudioMiniBar() {
	const {
		src,
		title,
		ticketId,
		isPlaying,
		currentTime,
		duration,
		playbackRate,
		toggle,
		seekRatio,
		cycleRate,
		stop,
	} = useGlobalAudio();
	const history = useHistory();

	const barRef = useRef(null);
	const dragRef = useRef({
		active: false,
		moved: false,
		offsetX: 0,
		offsetY: 0,
	});
	const [pos, setPos] = useState(() => {
		try {
			const raw = sessionStorage.getItem("globalAudioMiniPos");
			if (raw) {
				const parsed = JSON.parse(raw);
				if (
					Number.isFinite(parsed?.x) &&
					Number.isFinite(parsed?.y)
				) {
					return { x: parsed.x, y: parsed.y };
				}
			}
		} catch (_) {
			/* ignore */
		}
		return null;
	});

	useEffect(() => {
		if (!pos) return;
		try {
			sessionStorage.setItem("globalAudioMiniPos", JSON.stringify(pos));
		} catch (_) {
			/* ignore */
		}
	}, [pos]);

	useEffect(() => {
		const onPointerMove = (event) => {
			const drag = dragRef.current;
			if (!drag.active || !barRef.current) return;
			drag.moved = true;
			const rect = barRef.current.getBoundingClientRect();
			const maxX = Math.max(8, window.innerWidth - rect.width - 8);
			const maxY = Math.max(8, window.innerHeight - rect.height - 8);
			const nextX = Math.min(Math.max(8, event.clientX - drag.offsetX), maxX);
			const nextY = Math.min(Math.max(8, event.clientY - drag.offsetY), maxY);
			setPos({ x: nextX, y: nextY });
		};

		const onPointerUp = () => {
			dragRef.current.active = false;
		};

		window.addEventListener("pointermove", onPointerMove);
		window.addEventListener("pointerup", onPointerUp);
		window.addEventListener("pointercancel", onPointerUp);
		return () => {
			window.removeEventListener("pointermove", onPointerMove);
			window.removeEventListener("pointerup", onPointerUp);
			window.removeEventListener("pointercancel", onPointerUp);
		};
	}, []);

	if (!src) return null;

	const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
	const style = pos
		? { left: pos.x, top: pos.y, transform: "none" }
		: undefined;
	const label =
		ticketId != null && ticketId !== ""
			? `Áudio - #${ticketId}`
			: title || "Áudio";

	const startDrag = (event) => {
		if (event.button != null && event.button !== 0) return;
		if (event.target.closest("button, input, a, label")) return;
		const el = barRef.current;
		if (!el) return;
		const rect = el.getBoundingClientRect();
		dragRef.current = {
			active: true,
			moved: false,
			offsetX: event.clientX - rect.left,
			offsetY: event.clientY - rect.top,
		};
		if (!pos) {
			setPos({ x: rect.left, y: rect.top });
		}
		event.preventDefault();
	};

	const goToTicket = () => {
		if (ticketId == null || ticketId === "") return;
		history.push(`/tickets/${ticketId}`);
	};

	return (
		<div
			ref={barRef}
			className={`global-audio-mini${pos ? " global-audio-mini--moved" : ""}`}
			role="region"
			aria-label="Áudio em reprodução"
			style={style}
			onPointerDown={startDrag}
		>
			<span className="global-audio-mini__grip" aria-hidden title="Arrastar">
				<svg viewBox="0 0 16 16" fill="currentColor">
					<circle cx="5" cy="4" r="1.2" />
					<circle cx="11" cy="4" r="1.2" />
					<circle cx="5" cy="8" r="1.2" />
					<circle cx="11" cy="8" r="1.2" />
					<circle cx="5" cy="12" r="1.2" />
					<circle cx="11" cy="12" r="1.2" />
				</svg>
			</span>

			<button
				type="button"
				className="global-audio-mini__play"
				onClick={() => toggle(src, { title, ticketId })}
				aria-label={isPlaying ? "Pausar" : "Reproduzir"}
			>
				{isPlaying ? (
					<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
						<rect x="6" y="5" width="4" height="14" rx="1" />
						<rect x="14" y="5" width="4" height="14" rx="1" />
					</svg>
				) : (
					<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
						<path d="M8 5v14l11-7z" />
					</svg>
				)}
			</button>

			<div className="global-audio-mini__meta">
				<span className="global-audio-mini__title">{label}</span>
				<input
					type="range"
					min="0"
					max="100"
					step="0.1"
					value={progress}
					onChange={(e) => seekRatio(e.target.value)}
					className="global-audio-mini__range"
					aria-label="Progresso do áudio"
				/>
			</div>

			{ticketId != null && ticketId !== "" ? (
				<button
					type="button"
					className="global-audio-mini__ticket"
					onClick={goToTicket}
					aria-label={`Voltar para o ticket ${ticketId}`}
					title="Voltar para o ticket"
				>
					Voltar
				</button>
			) : null}

			<button
				type="button"
				className="global-audio-mini__speed"
				onClick={cycleRate}
				aria-label={`Velocidade ${playbackRate}x`}
			>
				{playbackRate}x
			</button>

			<span className="global-audio-mini__time">{formatTime(currentTime)}</span>

			<button
				type="button"
				className="global-audio-mini__close"
				onClick={stop}
				aria-label="Parar áudio"
				title="Parar"
			>
				×
			</button>
		</div>
	);
}
